/**
 * Qué parámetros expone cada tipo de regla.
 *
 * Se define aquí y no dentro del formulario para que agregar un detector nuevo
 * sea añadir una entrada, no tocar JSX. `path` es la ruta dentro del documento
 * de la regla: los que empiezan por `params.` van al campo libre que lee cada
 * detector.
 */

export const FIELD_DEFS = {
  threshold_attempts: {
    path: 'threshold',
    label: 'Intentos fallidos para alertar',
    type: 'number',
    min: 1,
    help: 'A partir de cuántas compras fallidas se genera la alerta.',
  },
  threshold_percentage: {
    path: 'threshold',
    label: 'Porcentaje de caída (%)',
    type: 'number',
    min: 1,
    max: 100,
    help: 'Se alerta cuando la caída supera este porcentaje.',
  },
  threshold_dependency: {
    path: 'threshold',
    label: 'Dependencia máxima por línea (%)',
    type: 'number',
    min: 1,
    max: 100,
    help: 'Se alerta cuando una sola línea concentra más de este porcentaje del volumen.',
  },
  threshold_concentration: {
    path: 'threshold',
    label: 'Concentración del líder (%)',
    type: 'number',
    min: 1,
    max: 100,
    help: 'Porcentaje de las ventas de la red generado por el líder mismo.',
  },
  threshold_variation: {
    path: 'threshold',
    label: 'Variación mínima a reportar (%)',
    type: 'number',
    min: 1,
    max: 100,
    help: 'Regiones con variación menor a este valor no se reportan.',
  },
  threshold_secondary: {
    path: 'threshold_secondary',
    label: 'Objetivo de ingresos (%)',
    type: 'number',
    min: 1,
    max: 100,
    help: 'Se calcula cuántas líneas hacen falta para acumular este porcentaje.',
  },
  period_days: {
    path: 'period_days',
    label: 'Ventana (días)',
    type: 'number',
    min: 1,
    help: 'Cuántos días hacia atrás se evalúan.',
  },
  comparison_period: {
    path: 'comparison_period',
    label: 'Período comparado',
    type: 'select',
    options: [
      { value: 'DAY', label: 'Día' },
      { value: 'WEEK', label: 'Semana' },
      { value: 'MONTH', label: 'Mes' },
    ],
    help: 'Siempre se comparan períodos cerrados contra el anterior equivalente.',
  },
  min_subject_volume_money: {
    path: 'min_subject_volume',
    label: 'Volumen mínimo para evaluar',
    type: 'number',
    min: 0,
    help: 'Piso de ruido: por debajo de este volumen la variación porcentual no significa nada.',
  },
  min_subject_volume_points: {
    path: 'min_subject_volume',
    label: 'Volumen mínimo (puntos)',
    type: 'number',
    min: 0,
    help: 'Redes con menos puntos que esto no se evalúan.',
  },
  min_network_size: {
    path: 'params.min_network_size',
    label: 'Tamaño mínimo de red',
    type: 'number',
    min: 1,
    help: 'Evita alertar sobre usuarios con uno o dos referidos.',
  },
  trend_after_days: {
    path: 'params.trend_after_days',
    label: 'Revisar tendencia desde (días)',
    type: 'number',
    min: 1,
    help: 'A partir de esta ventana se suma el volumen de los últimos 3 meses y se marca la red si viene bajando.',
  },
  min_children: {
    path: 'params.min_children',
    label: 'Directos mínimos',
    type: 'number',
    min: 2,
    help: 'Depender de un único directo es aritmética, no un hallazgo.',
  },
  min_rank_name: {
    path: 'params.min_rank_name',
    label: 'Rango mínimo',
    type: 'text',
    help: 'Sólo se reportan distribuidores de este rango en adelante.',
  },
  master_code: {
    path: 'params.master_code',
    label: 'Código maestro',
    type: 'text',
    help: 'Cabeza de toda la red. Sus directos son las líneas que se comparan.',
  },
  source: {
    path: 'params.source',
    label: 'Fuente de los intentos',
    type: 'select',
    options: [
      { value: 'ORDERS', label: 'Órdenes canceladas por fallo de pago' },
      { value: 'ATTEMPTS', label: 'Registro de intentos (exacto)' },
    ],
    help: 'ORDERS tiene histórico desde el día uno; ATTEMPTS es exacto pero sólo desde que se desplegó el registro.',
  },
  include_abandoned: {
    path: 'params.include_abandoned',
    label: 'Incluir carritos abandonados',
    type: 'switch',
    help: 'Suma órdenes creadas que nunca se pagaron. Apagado por defecto: incluye compras que aún se están pagando.',
  },
  scopes: {
    path: 'params.scopes',
    label: 'Ámbitos a evaluar',
    type: 'multiselect',
    options: [
      { value: 'GLOBAL', label: 'Global' },
      { value: 'REGION', label: 'Por región' },
    ],
  },
};

/** Campos comunes a toda regla, al final del formulario. */
export const COMMON_FIELDS = ['auto_resolve_days', 'notify_email', 'email_recipients'];

export const RULE_FIELDS = {
  FAILED_PURCHASE_ATTEMPTS: [
    'threshold_attempts',
    'period_days',
    'source',
    'include_abandoned',
  ],
  NETWORK_NO_GROWTH: ['period_days', 'min_network_size', 'trend_after_days'],
  SALES_DROP: [
    'threshold_percentage',
    'comparison_period',
    'min_subject_volume_money',
    'scopes',
  ],
  NETWORK_DEPENDENCY: [
    'threshold_dependency',
    'period_days',
    'min_subject_volume_points',
    'min_children',
    'min_rank_name',
  ],
  NETWORK_CONCENTRATION: [
    'threshold_concentration',
    'period_days',
    'min_subject_volume_money',
  ],
  PARETO_8020: ['threshold_secondary', 'period_days', 'master_code'],
  REGION_SALES_VARIATION: [
    'threshold_variation',
    'comparison_period',
    'min_subject_volume_money',
  ],
};

export const TYPE_LABELS = {
  FAILED_PURCHASE_ATTEMPTS: 'Intentos de compra fallidos',
  NETWORK_NO_GROWTH: 'Redes sin crecimiento',
  SALES_DROP: 'Caída abrupta en ventas',
  NETWORK_DEPENDENCY: 'Dependencia de red inferior',
  NETWORK_CONCENTRATION: 'Concentración de ventas',
  PARETO_8020: 'Principio de Pareto',
  REGION_SALES_VARIATION: 'Variación por región',
};

/** Lee un valor por ruta con punto ('params.min_children'). */
export const getByPath = (object, path) =>
  path.split('.').reduce((acc, key) => acc?.[key], object);

/** Devuelve una copia del objeto con el valor puesto en la ruta indicada. */
export const setByPath = (object, path, value) => {
  const [head, ...rest] = path.split('.');
  if (!rest.length) return { ...object, [head]: value };
  return {
    ...object,
    [head]: setByPath(object?.[head] ?? {}, rest.join('.'), value),
  };
};
